import { useEffect, useState } from "react";

import { AlertTriangle, Search } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import type { CodeRagSearch } from "..";
import { useRagSearch } from "../api";
import { FilterSelects } from "./FilterSelects";
import { HitCard } from "./HitCard";

const EXAMPLES = [
  "where subscription MRR is calculated for a contract",
  "how a survey response is persisted",
  "retry logic around the QuickBooks sync",
];

export function SearchTab({
  search,
  setSearch,
}: {
  search: CodeRagSearch;
  setSearch: (next: Partial<CodeRagSearch>) => void;
}) {
  const [draft, setDraft] = useState(search.q ?? "");

  // The URL is the source of truth for what's being searched, so the query runs
  // straight off it — no effect, and a linked or reloaded search just works.
  const { data, isFetching, error } = useRagSearch({
    query: search.q ?? "",
    repo: search.repo,
    language: search.lang,
    limit: 15,
  });

  // Keep the input in step when the URL changes from somewhere else (an example
  // chip, a repo link on the Overview tab, or the browser's back button).
  useEffect(() => {
    setDraft(search.q ?? "");
  }, [search.q]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = draft.trim();
    if (!q) return;
    setSearch({ q });
  };

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Describe what the code does — not the symbol name"
          className="min-w-64 flex-1"
        />
        <FilterSelects
          repo={search.repo}
          lang={search.lang}
          onChange={(next) => setSearch(next)}
          disabled={isFetching}
        />
        <Button type="submit" disabled={isFetching || !draft.trim()}>
          <Search className="mr-2 h-4 w-4" />
          {isFetching ? "Searching…" : "Search"}
        </Button>
      </form>

      {!search.q ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Semantic search finds code by what it <em>does</em>. For an exact
            symbol or error string, grep the repo instead — this index is for
            &ldquo;where do we handle X&rdquo; questions.
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {EXAMPLES.map((ex) => (
              <Button
                key={ex}
                variant="outline"
                size="sm"
                onClick={() => setSearch({ q: ex })}
              >
                {ex}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {isFetching ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : null}

      {error ? (
        <Alert variant="danger">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Search failed</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      ) : null}

      {data && !isFetching ? (
        <>
          <p className="text-sm text-muted-foreground">
            {data.hits.length} result{data.hits.length === 1 ? "" : "s"} in{" "}
            {data.took_ms} ms
            {search.repo ? ` · ${search.repo}` : ""}
            {search.lang ? ` · ${search.lang}` : ""}
          </p>
          <div className="flex flex-col gap-2">
            {data.hits.map((hit, i) => (
              <HitCard
                key={`${hit.repo}/${hit.file_path}:${hit.line_start}-${i}`}
                hit={hit}
                index={i + 1}
              />
            ))}
          </div>
          {data.hits.length === 0 ? (
            <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              No matches. Try describing the behaviour differently, or widen the
              repo and language filters.
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
